import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAttachFile, MdClose, MdMinimize, MdSend } from "react-icons/md";
import Avatar from "shared/components/Avatar";
import Input from "shared/components/Input";
import { RICH_TEXT_LAYOUT } from "shared/components/Input/RichText";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { SEND_EMAIL } from "utils/api/graphql/mutations/email";
import { PROCESS_CONFIG } from "utils/api/graphql/queries/process-config";
import {
  appendComposerSignatureToHtml,
  buildComposerSignatureHtml,
} from "utils/email/buildComposerSignatureHtml";
import { PROCESS_CONFIG_TYPES } from "utils/constants";
import { isValidEmailAddress } from "utils/helpers/validations";
import { COMPOSER_MODES, COMPOSER_STATUS, useEmailComposer } from "./context";
import TemplateSelector from "./TemplateSelector";

const EmailComposer = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const EMAIL = t("EMAIL_COMPOSER", { returnObjects: true });

  const {
    status,
    mode,
    onSend,
    fixedAttachments,
    to,
    cc,
    subject,
    body,
    attachments,
    contactId,
    projectId,
    project,
    contact,
    advisor,
    organization,
    closeComposer,
    minimizeComposer,
    restoreComposer,
    setSubject,
    setBody,
    setCc,
    addAttachment,
    removeAttachment,
  } = useEmailComposer();

  const [subjectError, setSubjectError] = useState(false);
  const [ccError, setCcError] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [ccInput, setCcInput] = useState("");
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const [sendEmail] = useMutation(SEND_EMAIL);
  const isManagement = mode === COMPOSER_MODES.MANAGEMENT;

  const tracerId = get(project, "tracer.id");
  const shouldLoadTracerProcess =
    !!tracerId && status !== COMPOSER_STATUS.CLOSED;

  const processConfigEmailWhere = useMemo(
    () =>
      tracerId
        ? {
            type: PROCESS_CONFIG_TYPES.EMAIL,
            process: {
              flows: {
                tracers: { id: tracerId },
              },
            },
          }
        : null,
    [tracerId],
  );

  const { data: processConfigEmailData } = useQuery(PROCESS_CONFIG, {
    variables: { where: processConfigEmailWhere },
    skip: !shouldLoadTracerProcess || !processConfigEmailWhere,
    fetchPolicy: "cache-first",
  });

  const contactName = `${get(contact, "user.firstname", "")} ${get(
    contact,
    "user.lastname",
    "",
  )}`.trim();

  const contactInitials = `${get(contact, "user.firstname", "")?.substring(
    0,
    1,
  )}${get(contact, "user.lastname", "")?.substring(0, 1)}`;

  const advisorName = `${get(advisor, "firstname", "")} ${get(
    advisor,
    "lastname",
    "",
  )}`.trim();
  const advisorPhone = get(advisor, "phone", "");
  const organizationName = get(organization, "name", "");

  const processEmailConfig = useMemo(() => {
    const fromQuery = get(processConfigEmailData, "processConfig");
    if (fromQuery) {
      return fromQuery;
    }
    const fromProject = get(project, "tracer.flow.process.config", []);
    const list = Array.isArray(fromProject) ? fromProject : [];
    return (
      list.find((entry) => entry?.type === PROCESS_CONFIG_TYPES.EMAIL) || null
    );
  }, [processConfigEmailData, project]);
  let processValue = get(processEmailConfig, "value");
  if (typeof processValue === "string") {
    try {
      const parsed = JSON.parse(processValue);
      if (parsed && typeof parsed === "object") {
        processValue = parsed;
      }
    } catch {
      /* keep string value */
    }
  }
  const rawProcessEmail =
    processValue && typeof processValue === "object"
      ? processValue.email
      : undefined;
  const normalizeComposerEmail = (raw) => {
    if (raw == null || raw === "") {
      return "";
    }
    if (typeof raw === "number" && raw === 0) {
      return "";
    }
    const str = String(raw).trim();
    return str;
  };
  const senderEmail =
    normalizeComposerEmail(rawProcessEmail) ||
    normalizeComposerEmail(get(organization, "config.emailConfig.email"));

  const senderInitials = organizationName
    ? organizationName
        .split(" ")
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase()
    : "";

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && status === COMPOSER_STATUS.OPEN) {
        closeComposer();
      }
    },
    [status, closeComposer],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        addAttachment({
          name: file.name,
          content: base64,
          contentType: file.type,
          size: file.size,
        });
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const buildCcRecipients = useCallback(() => {
    const unique = new Set();
    for (const addr of cc) {
      const trimmed = addr.trim();
      if (!trimmed) {
        continue;
      }
      if (!isValidEmailAddress(trimmed)) {
        return { ok: false };
      }
      unique.add(trimmed.toLowerCase());
    }
    const pending = ccInput.trim();
    if (pending) {
      if (!isValidEmailAddress(pending)) {
        return { ok: false };
      }
      unique.add(pending.toLowerCase());
    }
    return { ok: true, list: [...unique] };
  }, [cc, ccInput]);

  const handleSend = async () => {
    if (!subject.trim()) {
      setSubjectError(true);
      return;
    }
    setSubjectError(false);

    const ccResult = buildCcRecipients();
    if (!ccResult.ok) {
      setCcError(true);
      notification.error({
        message: EMAIL?.CC_INVALID_EMAIL || "Adresse e-mail en copie invalide",
        closeIcon: null,
      });
      return;
    }
    setCcError(false);
    setSending(true);

    const attachmentData =
      attachments.length > 0
        ? attachments.map(({ name, content, contentType }) => ({
            name,
            content,
            contentType,
          }))
        : undefined;

    try {
      const signatureLabelText =
        EMAIL?.SIGNATURE_LABEL || `Votre conseiller ${organizationName}`;
      const signatureHtml = buildComposerSignatureHtml({
        labelLine: signatureLabelText,
        advisorName,
        advisorPhone,
      });
      const htmlWithSignature = appendComposerSignatureToHtml(
        body || "",
        signatureHtml,
      );

      if (isManagement) {
        await onSend({ subject, html: htmlWithSignature });
        closeComposer();
        return;
      }

      await sendEmail({
        variables: {
          data: {
            contactId,
            projectId,
            to,
            cc: ccResult.list.length > 0 ? ccResult.list : undefined,
            subject,
            html: htmlWithSignature,
            attachments: attachmentData,
          },
        },
      });

      notification.success({
        message: `${EMAIL?.SENT_TO || "Email envoyé à"} ${contactName}`,
        closeIcon: null,
      });
      closeComposer();
    } catch (_err) {
      if (isManagement) return;
      notification.error({
        message: EMAIL?.SEND_ERROR || "Erreur lors de l'envoi",
        closeIcon: null,
      });
    } finally {
      setSending(false);
    }
  };

  const handleAddCc = () => {
    const trimmed = ccInput.trim();
    if (!trimmed) {
      setCcError(false);
      setCcInput("");
      return;
    }
    if (!isValidEmailAddress(trimmed)) {
      setCcError(true);
      return;
    }
    setCcError(false);
    const normalized = trimmed.toLowerCase();
    if (!cc.some((e) => e.trim().toLowerCase() === normalized)) {
      setCc([...cc, normalized]);
    }
    setCcInput("");
  };

  const handleRemoveCc = (email) => {
    setCc(cc.filter((c) => c !== email));
  };

  if (status === COMPOSER_STATUS.CLOSED) return null;

  if (status === COMPOSER_STATUS.MINIMIZED) {
    return (
      <div
        className="email-composer email-composer--minimized"
        onClick={restoreComposer}
      >
        <div className="email-composer__header">
          <span className="email-composer__header-title">
            {EMAIL?.NEW_MESSAGE || "Nouveau message"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="email-composer__overlay" onClick={closeComposer} />
      <div className="email-composer email-composer--open">
        <div className="email-composer__header">
          <span className="email-composer__header-title">
            {EMAIL?.NEW_MESSAGE || "Nouveau message"}
          </span>
          <div className="email-composer__header-actions">
            <MdMinimize
              className="email-composer__header-icon"
              onClick={minimizeComposer}
            />
            <MdClose
              className="email-composer__header-icon"
              onClick={closeComposer}
            />
          </div>
        </div>

        <div className="email-composer__content">
          <div className="email-composer__field">
            <span className="email-composer__field-label">
              {EMAIL?.FROM || "De"}
            </span>
            <div className="email-composer__field-value">
              <Avatar mode="text" size="small" value={senderInitials} />
              <span className="email-composer__field-name">
                {organizationName}
              </span>
              <span className="email-composer__field-email">
                {senderEmail ? `${senderEmail}` : ""}
              </span>
            </div>
          </div>

          <div className="email-composer__field">
            <span className="email-composer__field-label">
              {EMAIL?.TO || "À"}
            </span>
            <div className="email-composer__field-value">
              {!isManagement && (
                <>
                  <Avatar mode="text" size="small" value={contactInitials} />
                  <span className="email-composer__field-name">
                    {contactName}
                  </span>
                </>
              )}
              <span className="email-composer__field-email">
                {to.join(", ")}
              </span>
              {!showCc && !isManagement && (
                <span
                  className="email-composer__add-cc"
                  onClick={() => setShowCc(true)}
                >
                  {EMAIL?.ADD_CC || "Ajouter Cc"}
                </span>
              )}
            </div>
          </div>

          {showCc && (
            <div className="email-composer__field">
              <span className="email-composer__field-label">Cc</span>
              <div className="email-composer__cc-container">
                {cc.map((email) => (
                  <span key={email} className="email-composer__cc-tag">
                    {email}
                    <MdClose
                      className="email-composer__cc-remove"
                      onClick={() => handleRemoveCc(email)}
                    />
                  </span>
                ))}
                <input
                  className={classNames("email-composer__cc-input", {
                    "email-composer__cc-input--error": ccError,
                  })}
                  value={ccInput}
                  onChange={(e) => {
                    setCcInput(e.target.value);
                    if (ccError) setCcError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCc();
                    }
                  }}
                  onBlur={handleAddCc}
                  placeholder={
                    EMAIL?.CC_PLACEHOLDER || "Ajouter un destinataire"
                  }
                />
              </div>
            </div>
          )}

          <div className="email-composer__field">
            <input
              className={classNames("email-composer__subject-input", {
                "email-composer__subject-input--error": subjectError,
              })}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                if (subjectError) setSubjectError(false);
              }}
              placeholder={EMAIL?.SUBJECT_PLACEHOLDER || "Objet"}
            />
          </div>

          {!isManagement && <TemplateSelector />}

          <Input
            type="richText"
            layout={RICH_TEXT_LAYOUT.EMBED}
            className="email-composer__editor"
            value={body}
            onChange={setBody}
            placeholder={
              EMAIL?.BODY_PLACEHOLDER || "Rédigez votre message ici..."
            }
          />

          <div className="email-composer__signature">
            <p>
              {EMAIL?.SIGNATURE_LABEL || `Votre conseiller ${organizationName}`}
            </p>
            <p className="email-composer__signature-name">{advisorName}</p>
            <p className="email-composer__signature-phone">{advisorPhone}</p>
          </div>
        </div>

        {fixedAttachments.length > 0 && (
          <div className="email-composer__attachments">
            {fixedAttachments.map((name) => (
              <span key={name} className="email-composer__attachments-chip">
                {name}
              </span>
            ))}
          </div>
        )}

        {attachments.length > 0 && (
          <div className="email-composer__attachments">
            {attachments.map((file, idx) => (
              <span
                key={`${file.name}-${idx}`}
                className="email-composer__attachments-chip"
              >
                {file.name}
                <MdClose
                  className="email-composer__attachments-remove"
                  onClick={() => removeAttachment(idx)}
                />
              </span>
            ))}
          </div>
        )}

        <div className="email-composer__footer">
          {!isManagement && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="email-composer__file-input"
                onChange={handleFileSelect}
              />
              <MdAttachFile
                className="email-composer__attach-btn"
                onClick={() => fileInputRef.current?.click()}
              />
            </>
          )}
          <div className="email-composer__footer-actions">
            <button
              className="email-composer__btn email-composer__btn--cancel"
              onClick={closeComposer}
            >
              {EMAIL?.CANCEL || "Annuler"}
            </button>
            <button
              className="email-composer__btn email-composer__btn--send"
              onClick={handleSend}
              disabled={sending}
            >
              <MdSend />
              {sending
                ? EMAIL?.SENDING || "Envoi..."
                : EMAIL?.SEND || "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailComposer;
